import {_} from 'lodash';
import {IOrganization, IRouteDefinition, IRouteDefinitionParams, IRouteParams, TRouteId} from "../index";
import {ObjectTypeId, Permission} from "../constants";
import {RequestRouteData} from "../req/RequestRouteData";
import {TabManager} from "../component/tab-list/TabManager";
import {getEadmRole, getNextId, getObjectDefinition, IObjectDefinition} from "./misc";
import {getRouteDefinition} from "../constants/routeLookup";
import {getRouteHash} from "../constants/routeHash";
import {global} from "../appContext";
import {Tab} from "../component/tab-list/Tab";
import pubsub from "../pubsub";
import {IDialogOpt} from "../component/dialog/manager";
import {nsLogin} from "./nsLogin";

export namespace nsRoute {

    export interface IParsedRouteParams {
        routeId: TRouteId
        requestRouteData?: RequestRouteData
        routeDef: IRouteDefinition
        routeParams?: IRouteParams
        tab?: Tab
        tabManager?: TabManager
    }

    export enum ButtonOrder {
        CANCEL = 1,
        EXIT,
        EMULATE_USER,
        DISCARD,
        PREVIEW,
        ADD_MEMBER,
        PASSWORD,
        SEND_PASSWORD_BY_SMS,
        SEND_MESSAGE,
        COPY,
        CREATE,
        CREATE_WIZARD,
        CREATE_FROM_OBJECT,
        EDIT_PERMISSIONS,
        EDIT_WIZARD,
        EDIT,
        DELETE,
        MENU,
        START_OVER,
        DETAILS,
        PREVIOUS,
        BACK,
        NEXT,
        CONTINUE,
        SUBMIT,
        BOOKMARK,
        PARENT,
        HELP,
        SPLIT_BUTTON,
    }

    /* Extend this class to get implementation of all parsed route params */
    export abstract class ParsedRouteDataDecorator {
        protected __objDef?: IObjectDefinition
        protected __objectTypeId?: ObjectTypeId

        protected readonly __id?: number;
        protected readonly __orgnr?: string;

        public readonly __dialog?: IDialogOpt;
        protected readonly __requestRouteData?: RequestRouteData;
        protected readonly __routeDef!: IRouteDefinition;
        protected readonly __routeId!: TRouteId;
        protected readonly __routeParams?: IRouteParams;
        protected readonly __tab?: Tab;
        protected __tabManager?: TabManager;
        protected readonly __shellRouteDef?: IRouteDefinition | null;
        protected readonly __shellRouteId?: TRouteId | null;
        protected readonly __tabRouteDef?: IRouteDefinition | null;

        protected readonly __hashParent?: string;
        protected readonly __hashChild?: string;
        protected readonly __hashParentMin?: string;
        protected readonly __hashEdit?: string;
        protected readonly __hashEditMin?: string;
        protected readonly __hashCreate?: string;
        protected readonly __hashCreateMin?: string;
        protected readonly __componentId: string;

        protected readonly __showHeadingButtons!: boolean
        protected readonly __showPageButtons!: boolean
        protected readonly __isDialog!: boolean

        constructor(protected __routeDefinitionParams__: IRouteDefinitionParams = {}) {
            decorateParsedRouteParams(this, __routeDefinitionParams__);
            this.__componentId = __routeDefinitionParams__.__componentId ?? this.__routeId + '_' + getNextId();
        }

        protected __getParams(optionalParams = {}): IRouteDefinitionParams & any {
            return _.defaultsDeep(optionalParams, this.__routeDefinitionParams__);
        }

        /**
         * Returns the component itself. The element should include this binding:
         * @example data-bind="attr: {id: __componentId}"
         * @protected
         */
        protected __getComponentElement(): HTMLElement {
            const el = document.getElementById(this.__componentId);
            if (!el) {
                throw new Error(`Component element not found: ${this.__componentId}`);
            }
            return el;
        }

        /**
         * Returns an element within the component.
         * @protected
         */
        protected __queryComponentSelector(selector: string): HTMLElement {
            const el = this.__getComponentElement().querySelector(selector);
            if (!el) {
                throw new Error(`Selector not found in component ${this.__componentId}: ${selector}`);
            }
            return el as HTMLElement;
        }

        dispose() {
            pubsub.off(this);
            // Do not abort shared PromiseRequest here — tab panel dispose would cancel
            // getobject for the still-mounted shell. Router aborts on hard navigation.
        }
    }

    export function decorateParsedRouteParams(self: any, params: Partial<IRouteDefinitionParams> = {}) {
        const parsed = getParsedRouteParams(params);
        self.__routeDef = parsed.routeDef;
        self.__routeId = parsed.routeId;
        self.__routeParams = parsed.routeParams;
        self.__tab = parsed.tab;
        self.__tabManager = parsed.tabManager;
        self.__requestRouteData = parsed.requestRouteData;
        self.__objectTypeId = params.__objectTypeId ?? self.__objectTypeId ?? parsed.routeDef.objectTypeId;
        self.__objDef = self.__objectTypeId ? getObjectDefinition(self.__objectTypeId) : null;
        const propParent = parsed.routeDef.parent;
        self.__hashParent = propParent ? getRouteHash('parent', self.__routeDef, self.__routeParams, true) : null;
        self.__hashParentMin = propParent ? getRouteHash('parent', self.__routeDef, self.__routeParams, false) : null;
        const propChild = parsed.routeDef.child;
        self.__hashChild = propChild ? getRouteHash('child', self.__routeDef, self.__routeParams, true) : null;
        const propEdit = parsed.routeDef.edit;
        self.__hashEdit = propEdit ? getRouteHash('edit', self.__routeDef, self.__routeParams, true) : null;
        self.__hashEditMin = propEdit ? getRouteHash('edit', self.__routeDef, self.__routeParams, false) : null;
        const propCreate = parsed.routeDef.create;
        self.__hashCreate = propCreate ? getRouteHash('create', self.__routeDef, self.__routeParams, true) : null;
        self.__hashCreateMin = propCreate ? getRouteHash('create', self.__routeDef, self.__routeParams, false) : null;
        self.__orgnr = self.__routeParams?.orgnr ?? self.__routeParams?.orgid ?? global.login.selectedOrganizationId();
        self.__id = parsed.routeParams?.['id'] ? Number(parsed.routeParams['id']) : undefined;
        self.__dialog = params.__dialog
        self.__isDialog = !!params.__dialog

        self.__showHeadingButtons = self.__routeDef.showHeadingButtons !== false
        self.__showPageButtons = self.__routeDef.showPageButtons !== false
        self.__shellRouteDef = params.__shellRouteDef ?? null
        self.__shellRouteId = params.__shellRouteId ?? null
        self.__tabRouteDef = params.__tabRouteDef ?? null
    }

    function getParsedRouteParams(params: Partial<IRouteDefinitionParams> = {}): IParsedRouteParams {
        const router = global.router;
        const cr = router.currentRoute();

        let tabManager: TabManager | undefined;
        if (params.__tabManager) {
            tabManager = params.__tabManager;
        }

        let routeId: TRouteId | undefined = params.__routeId;

        let routeDef: IRouteDefinition | null | undefined;
        if (params.__shellRouteDef) {
            routeDef = params.__shellRouteDef;
        } else if (params.__routeDef) {
            routeDef = params.__routeDef;
        } else if (params.__routeId) {
            routeDef = getRouteDefinition(params.__routeId);
        } else if (tabManager) {
            const tabId = tabManager.currentId();
            routeDef = tabId ? getRouteDefinition(tabId) : undefined;
        } else {
            routeDef = cr?.__routeDef;
        }

        if (!routeDef) {
            throw new Error('Unable to resolve route definition');
        }

        if (!routeId) {
            routeId = routeDef.id;
        }

        const routeParams: IRouteParams = params.__routeParams ?? cr?.__routeParams ?? {};

        let requestRouteData: RequestRouteData | undefined;
        if (params.__requestRouteData) {
            // Prefer the shell/router instance so tab swaps share one getobject wrapper.
            requestRouteData = params.__requestRouteData;
        } else if (routeDef.fetchRouteDef) {
            requestRouteData = new RequestRouteData(routeDef, routeParams);
        } else if (params.__shellRouteDef?.fetchRouteDef) {
            requestRouteData = new RequestRouteData(params.__shellRouteDef, routeParams);
        }

        const tab = params.__tab ?? tabManager?.getTab(routeId) ?? undefined;

        return {
            ...(requestRouteData ? {requestRouteData} : {}),
            routeDef,
            routeId,
            routeParams,
            ...(tab ? {tab} : {}),
            ...(tabManager ? {tabManager} : {}),
        };
    }



    export function isRouteLicenseOkay(routeDef: IRouteDefinition, org: IOrganization): boolean {
        if (!org || !routeDef?.license) {
            return true
        }
        switch (routeDef.license) {
            case "ehub": {
                return org.ehub;
            }
            case 'apiusers': {
                const permission = getEadmRole(org)
                if (permission >= Permission.OrganizationAdmin) {
                    return true
                }
                if (permission == Permission.CompanyAdmin) {
                    return org.enableapiusersforcompanyadmins
                }
                return false
            }
            case 'brreg':
                return org.hasimportbrreg
            default:
                return true;
        }
    }

}
